import React, { useEffect } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import useProductInformation from '@storefront/hooks/useProductInformation';
import { NamespacesI18n } from '@storefront/i18n';
import ModalSelection from '@storefront/app/v2/pdp-config/shared-components/modals/modal-selection/modal-selection';
import { getRegularPrice } from '@storefront/utils/bundlePriceCalculator';
import {
  getProductImage,
  getProductSmallImage,
} from '@storefront/utils/prodRequests';

import { isZeroDollarPaperPlan } from '../../utils/utils';
import OptionsColumns from '../options-columns/options-columns';
import styles from '../product-configuration-visid.module.scss';
import {
  BundleItemOptionWithLowestPrice,
  BundleItemWithLowestPrice,
} from '../product-configuration.utils';
import ImageRenderer from '../render-image';
import { getOptionToShow } from './not-required-options.utils';

interface RenderNotRequiredOptionsProps {
  showSmallImage: boolean;
  loadingImage: boolean;
  setLoadingImage: (loading: boolean) => void;
  skuList: string[];
}

export default function RenderNotRequiredOptions(
  props: RenderNotRequiredOptionsProps
) {
  const { showSmallImage, loadingImage, setLoadingImage, skuList } = props;
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);

  const { activeSKU, bundleProduct, bundleOptions, totalSteps, configuration } =
    useProductInformation(skuList);

  const isPrintPlanSelected = bundleOptions?.visibleSimpleOptions?.some(
    (option) =>
      Object.values(configuration?.[activeSKU] || {}).some((selectedUid) =>
        option.options.some(
          (opt: BundleItemOptionWithLowestPrice) => opt.uid === selectedUid
        )
      )
  );

  const getImageRenderer = (option: BundleItemWithLowestPrice, id: string) => {
    const imageSizeHandler = showSmallImage
      ? getProductSmallImage(option)
      : getProductImage(option);

    return (
      <ImageRenderer
        src={imageSizeHandler}
        title={option.title}
        id={id}
        loadingImage={loadingImage}
        setLoadingImage={setLoadingImage}
        sku={bundleProduct?.sku}
        disabled={!activeSKU}
      />
    );
  };

  const containerId = 'not-required-options-container';

  const validBundleOptions = bundleOptions.notRequiredOptions.filter(
    (bundleOption: BundleItemWithLowestPrice) => {
      const optionToShow = getOptionToShow(
        bundleOption,
        configuration,
        activeSKU,
        bundleProduct
      );

      // only include bundle options that have valid options with non-zero prices
      return optionToShow?.uid && getRegularPrice(optionToShow) > 0;
    }
  );

  return validBundleOptions.map(
    (bundleOption: BundleItemWithLowestPrice, index) => {
      const optionToShow = getOptionToShow(
        bundleOption,
        configuration,
        activeSKU,
        bundleProduct
      );

      return (
        <div
          key={bundleOption.title}
          className={styles['products-options-container']}
          id={containerId}
        >
          {!showSmallImage &&
            getImageRenderer(bundleOption, 'bundleOptionsImage')}
          <div
            className={classNames(
              styles['options'],
              !activeSKU && styles['options-disabled']
            )}
            id="add-paper-section"
            data-testid="add-paper-section-test"
          >
            <div className={styles['label-container']}>
              <span
                className={classNames(styles['description'], 'caption-regular')}
              >
                {t('radioButtonGroup.counter', {
                  ith: index + 2 + bundleOptions.visibleSimpleOptions.length,
                  total: totalSteps,
                })}{' '}
              </span>
            </div>
            <span className={classNames(styles['title'], 'subtitle-large')}>
              {t('pdpConfig.printInkBundle.paperSelectionTitle')}
            </span>
            {showSmallImage &&
              getImageRenderer(bundleOption, 'bundleOptionsTabletImage')}
            <div className={styles['options-column-container']}>
              {optionToShow?.uid && (
                <OptionsColumns
                  key={optionToShow.uid}
                  bundleOptionUid={bundleOption.uid}
                  option={optionToShow}
                  skuList={skuList}
                  isUIDSelected={isPrintPlanSelected}
                  parentId={containerId}
                  zeroDollarUid={
                    isZeroDollarPaperPlan(bundleOption)
                      ? bundleOption.options[0].uid
                      : ''
                  }
                />
              )}
            </div>
            <ModalSelection
              modalName={bundleOption.options[0].product.hp_product_type_label}
              isDisabled={!activeSKU}
            />
          </div>
        </div>
      );
    }
  );
}
